import React, { useState } from 'react';

/**
 * Colis d'une préparation.
 *
 * RÈGLE : une caisse a TOUJOURS un numéro — scanné, ou tapé à la main quand
 * le scan ne marche pas (vieux téléphone). Seul un carton n'a pas de numéro.
 *
 * Avant, cet écran proposait un compteur « caisses sans étiquette » qui
 * permettait de déclarer un nombre de caisses sans aucun code. Une caisse
 * comptée ainsi n'entrait ni dans le stock ni dans les mouvements : elle
 * sortait du dépôt sans exister. Sur 30 jours, 833 caisses sont parties
 * ainsi — une sur cinq. Le compteur a été retiré : pour ajouter une caisse,
 * on passe par le scan ou la saisie du numéro, juste au-dessus.
 */

function Counter({ label, emoji, value, setValue, color, borderColor }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  return (
    <div style={{ flex: 1, padding: '10px', background: 'white', borderRadius: 10, border: '0.5px solid #E8E8EE', textAlign: 'center' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: borderColor, marginBottom: 6 }}>{emoji} {label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <button onClick={() => setValue(Math.max(0, value - 1))} disabled={value === 0} style={{
          width: 34, height: 34, borderRadius: '50%', border: '1.5px solid #E8E8EE',
          background: 'white', fontSize: 18, fontWeight: 700, color: value === 0 ? '#E5E7EB' : '#C0392B',
          cursor: value === 0 ? 'default' : 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>−</button>
        <input type="text" inputMode="numeric" pattern="[0-9]*"
          value={editing ? draft : value}
          onFocus={(e) => { setEditing(true); setDraft(String(value)); e.target.select(); }}
          onChange={(e) => { setDraft(e.target.value.replace(/[^0-9]/g, '')); }}
          onBlur={() => { setValue(Math.max(0, parseInt(draft) || 0)); setEditing(false); }}
          style={{ width: 50, textAlign: 'center', fontSize: 24, fontWeight: 700, color: borderColor, border: '1.5px solid #E8E8EE', borderRadius: 8, padding: '4px 2px', fontFamily: 'inherit', background: value > 0 ? color : '#FFF' }} />
        <button onClick={() => setValue(value + 1)} style={{
          width: 34, height: 34, borderRadius: '50%', border: '1.5px solid ' + borderColor,
          background: color, fontSize: 18, fontWeight: 700, color: borderColor,
          cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>+</button>
      </div>
    </div>
  );
}

export default React.memo(function PrepCaissesSection({
  session, user, caisses = [], nbAmb, nbFroid, nbCartons,
  onUpdateCartons,
}) {

  const scanAmb = caisses.filter(c => c.type === 'ambiant').length;
  const scanFroid = caisses.filter(c => c.type === 'froid').length;
  const totalCaisses = scanAmb + scanFroid;
  // Caisses déclarées autrefois sans numéro : on les signale au lieu de les cacher
  const orphelines = Math.max(0, (nbAmb + nbFroid) - totalCaisses);

  return (
    <>
      {/* ═══ Caisses : identifiées une par une ═══ */}
      {totalCaisses > 0 && (
        <div style={{ padding: '8px 12px', background: '#18181A', borderRadius: 8, display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#85B7EB', fontSize: 18, fontWeight: 700 }}>{scanAmb}</div>
            <div style={{ color: '#85B7EB', fontSize: 9, fontWeight: 600 }}>AMB</div>
          </div>
          <div style={{ color: '#444', fontSize: 14 }}>+</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#5DCAA5', fontSize: 18, fontWeight: 700 }}>{scanFroid}</div>
            <div style={{ color: '#5DCAA5', fontSize: 9, fontWeight: 600 }}>FROID</div>
          </div>
          <div style={{ color: '#444', fontSize: 14 }}>=</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'white', fontSize: 18, fontWeight: 700 }}>{totalCaisses}</div>
            <div style={{ color: '#999', fontSize: 9, fontWeight: 600 }}>CAISSES</div>
          </div>
        </div>
      )}

      {orphelines > 0 && (
        <div style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 10, background: '#FDECEA', border: '1.5px solid #C0392B', fontSize: 11, color: '#A3271C', lineHeight: 1.5 }}>
          ⚠️ <b>{orphelines} caisse(s) comptée(s) sans numéro</b> sur cette préparation.
          Ajoute leur numéro ci-dessus (scan ou saisie) : une caisse sans numéro ne peut pas être réclamée à la pharmacie.
        </div>
      )}

      {totalCaisses === 0 && orphelines === 0 && (
        <div style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 10, background: '#F7F7F9', fontSize: 11, color: '#6B7280', lineHeight: 1.5 }}>
          Aucune caisse pour l’instant. Scanne son numéro ci-dessus, ou tape-le à la main si le scan ne marche pas.
        </div>
      )}

      {/* ═══ Cartons — le seul colis sans numéro ═══ */}
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 6 }}>Cartons</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <Counter label="CARTONS" emoji="📦" value={nbCartons} setValue={(v) => onUpdateCartons(v - nbCartons)} color="#E6F1FB" borderColor="#1A5276" />
      </div>

      {/* ⚠️ Rappel bon de transport */}
      <div style={{
        padding: '8px 12px', borderRadius: 8, marginTop: 8,
        background: '#FEF3CD', border: '0.5px solid #F0C36D',
        fontSize: 11, color: '#92710C', lineHeight: 1.5,
      }}>
        ⚠️ Ces chiffres servent à générer les bons de transport. Merci de vérifier avant de valider.
      </div>
    </>
  );
});
